import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import { BackgroundColorI, StrapiImage, TitleType } from "../Types";
import { getTitleHTMLTag } from "../../helpers/tools";
import Button from "../Inputs/Button";
import { useDevice } from "../Hooks";
import SimpleMarkdown from "../SimpleMarkdown/SimpleMarkdown";
import { fixStrapiUrl } from "../../helpers/strapiUrl";
import ResponsiveImage from "../Images/ResponsiveImage";

interface ImageCardsI {
  data: {
    BackgroundColor: BackgroundColorI;
    CardsContent: {
      CardImage: StrapiImage;
      CardImageAlt: string;
      CardImageURL: string;
      CardText: string;
      CardTitle: string;
      CardTitleHeading: {
        TitleTypeSelection: TitleType;
      };
      GhostButtonID: string;
      GhostButtonLabel: string;
      GhostButtonURL: string;
      GhostNewTab: boolean;
      GhostNoFollow: boolean;
      HasGhostButton: boolean;
      HasPrimaryButton: boolean;
      PrimaryButtonID: string;
      PrimaryButtonLabel: string;
      PrimaryButtonURL: string;
      PrimaryNewTab: boolean;
      PrimaryNoFollow: boolean;
    }[];
    HasSubtitle: boolean;
    HasTitle: boolean;
    SansTitle: string;
    SerifTitle: string;
    Subtitle: string;
    TitleHeading: {
      TitleTypeSelection: TitleType;
    };
  };
}

const ImageCards = ({ data }: ImageCardsI) => {
  const { isMobile } = useDevice();
  const {
    BackgroundColor,
    CardsContent,
    HasSubtitle,
    HasTitle,
    SansTitle,
    SerifTitle,
    Subtitle,
    TitleHeading,
  } = data;

  const backgroundColor = {
    Blue_Gray: "#F5F7FA",
    Light_Blue: "#ECF0FF",
    White: "#FFFFFF",
  }[BackgroundColor.BackgroundColor || "White"];

  const cardBackgroundColor = {
    Blue_Gray: "#FFFFFF",
    Light_Blue: "#FFFFFF",
    White: "#F5F7FA",
  }[BackgroundColor.BackgroundColor || "White"];

  const titleC = HasTitle
    ? getTitleHTMLTag(
        <>
          <span className="font-DM font-bold H2">{SansTitle} </span>
          <span className="font-MW font-light H2">{SerifTitle}</span>
        </>,
        TitleHeading?.TitleTypeSelection,
        "text-[#424242] md:text-center"
      )
    : null;

  const subtitleC = HasSubtitle
    ? getTitleHTMLTag(
        <span className="font-DM font-medium H4">{Subtitle}</span>,
        "p",
        "text-[#424242] md:text-center"
      )
    : null;

  return (
    <section className="overflow-hidden" style={{ backgroundColor }}>
      <div className="flex flex-col max-w-content mx-auto py-[64px] xl:px-[0px] px-[24px] md:gap-[64px] gap-[32px]">
        <div className="flex flex-col md:gap-[16px] gap-[8px]">
          {titleC}
          {subtitleC}
        </div>
        {CardsContent && CardsContent.length > 0 ? (
          isMobile ? (
            <>
              <Swiper
                slidesPerView={1.15}
                spaceBetween={16}
                freeMode={true}
                pagination={{ clickable: true, el: ".swiper-image-cards-pagination" }}
                modules={[FreeMode, Pagination]}
                className="!w-full"
              >
                {[...CardsContent].map((content, idx) => (
                  <SwiperSlide key={`${content.CardTitle}_${idx}`}>
                    <CardContent
                      data={content}
                      backgroundColor={cardBackgroundColor}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="swiper-image-cards-pagination flex justify-center gap-[5px] mt-[16px]" />
            </>
          ) : (
            <div className="flex flex-row flex-wrap gap-[32px] justify-center">
              {[...CardsContent].map((content, idx) => {
                return (
                  <CardContent
                    data={content}
                    backgroundColor={cardBackgroundColor}
                    key={`${content.CardTitle}_${idx}`}
                  />
                );
              })}
            </div>
          )
        ) : null}
      </div>
    </section>
  );
};

const CardContent = ({ data, backgroundColor }: any) => {
  const {
    CardImage,
    CardImageAlt,
    CardImageURL,
    CardText,
    CardTitle,
    CardTitleHeading,
    GhostButtonID,
    GhostButtonLabel,
    GhostButtonURL,
    GhostNewTab,
    GhostNoFollow,
    HasGhostButton,
    HasPrimaryButton,
    PrimaryButtonID,
    PrimaryButtonLabel,
    PrimaryButtonURL,
    PrimaryNewTab,
    PrimaryNoFollow,
  } = data;

  const titleC = getTitleHTMLTag(
    CardTitle,
    CardTitleHeading?.TitleTypeSelection,
    "text-[#424242] H4 font-bold text-left w-full"
  );

  return (
    <div
      className="rounded-[24px] flex-[352px] max-w-[352px] overflow-hidden flex flex-col"
      style={{ backgroundColor }}
    >
      <ResponsiveImage
        src={CardImage?.url ? fixStrapiUrl(CardImage?.url) : CardImageURL}
        alt={CardImageAlt}
        className="w-full md:h-[154px] h-[120px] object-cover object-center"
        sizes="(min-width:768px) 352px, 78vw"
        loading="lazy"
        width={352}
        height={154}
      />
      <div className="p-[16px_24px_24px] flex flex-col gap-[16px] flex-1">
        {CardTitle ? titleC : null}
        {CardText ? (
          <SimpleMarkdown className="BodyL font-normal text-[#555555] md:[&_p:not(:last-child)]:mb-[24px] [&_p:not(:last-child)]:mb-[12px]">
            {CardText}
          </SimpleMarkdown>
        ) : null}
        {HasPrimaryButton || HasGhostButton ? (
          <div className="mt-auto pt-[16px] space-y-[8px]">
            {HasPrimaryButton ? (
              <Button
                label={PrimaryButtonLabel}
                href={PrimaryButtonURL}
                ghost={false}
                newTab={PrimaryNewTab}
                noFollow={PrimaryNoFollow}
                id={PrimaryButtonID}
                rightIcon="ArrowRightIcon"
                size="40"
                className="!w-fit mx-auto"
              />
            ) : null}
            {HasGhostButton ? (
              <Button
                label={GhostButtonLabel}
                href={GhostButtonURL}
                ghost={true}
                secondary={true}
                newTab={GhostNewTab}
                noFollow={GhostNoFollow}
                id={GhostButtonID}
                size="40"
                className="!w-fit mx-auto"
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ImageCards;
